import { TOOL_NAMES } from 'js/src/agent/toolNames';
import React from 'react';
import { useSelector } from 'react-redux';
import { z } from 'zod';

import { Urls } from 'js/src/client';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import { Toast } from 'js/src/components/common/Toast';
import { getCanTranslateVideos } from 'js/src/data/account';
import { getVideoId } from 'js/src/data/app';
import { getTranslationSelectValues } from 'js/src/data/translations';
import type { TranslationSelectValue } from 'js/src/data/translations';
import { isVideoLimitError } from 'js/src/hooks/video-limit-error';
import type { RootState } from 'js/src/libs/RootState';
import Navigation from 'js/src/libs/navigation';
import { duplicateVideosOperation } from 'js/src/luminary/dashboard-luminary-operations';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolExecutionContext,
} from '../types';
import { Tool } from './Tool';

export const translateVideoSchema = z.object({
    target_language: z
        .string()
        .optional()
        .describe(
            "The language to translate the video into, as the user named it (e.g. 'French', " +
                "'Canadian French', 'Spanish', or a locale code like 'fr-CA'). When this matches a " +
                'supported language the translation starts automatically; otherwise the user picks ' +
                'the language in a dialog. Omit it only if the user did not say which language.',
        ),
});
export type TranslateVideoArgs = z.infer<typeof translateVideoSchema>;

/**
 * Best-effort match of a user-named language to a supported translation option,
 * by locale code, exact label, or label containment either way.
 */
export function resolveLanguageOption(
    options: readonly TranslationSelectValue[],
    query: string | undefined,
): TranslationSelectValue | undefined {
    if (!query) {
        return undefined;
    }
    const q = query.trim().toLowerCase();
    if (!q) {
        return undefined;
    }
    return (
        options.find((option) => option.value.toLowerCase() === q) ||
        options.find((option) => optionLabel(option).toLowerCase() === q) ||
        options.find((option) => optionLabel(option).toLowerCase().includes(q)) ||
        options.find((option) => q.includes(optionLabel(option).toLowerCase()))
    );
}

/** The option's display label as a string (it is always a language name), falling back to its code. */
function optionLabel(option: TranslationSelectValue): string {
    return typeof option.label === 'string' ? option.label : option.value;
}

/**
 * Renders a button the user clicks to open the translated copy. The copy is
 * duplicated and opened from that click so the new tab carries a user gesture
 * and is not popup-blocked (the agent turn is too far from the last click).
 */
const TranslateVideoToolRenderer: React.FC<CustomRendererComponentProps> = ({
    result,
    toolCall,
}) => {
    const videoId = useSelector(getVideoId);
    const options = useSelector((state: RootState) => getTranslationSelectValues(state, false));
    const { duplicateVideos } = useLuminaryOperations({ duplicateVideosOperation });
    const [isOpening, setIsOpening] = React.useState(false);

    const targetLanguage = React.useMemo(() => {
        try {
            const args = toolCall?.function?.arguments
                ? (JSON.parse(toolCall.function.arguments) as TranslateVideoArgs)
                : undefined;
            return args?.target_language;
        } catch {
            return undefined;
        }
    }, [toolCall]);

    const languageOption = resolveLanguageOption(options, targetLanguage);

    const handleOpenClick = React.useCallback(() => {
        if (videoId === null || isOpening) {
            return;
        }
        setIsOpening(true);
        analytics.track('Toolbox: Translate Video Clicked', {
            target_language: languageOption?.value ?? null,
        });
        duplicateVideos([videoId])
            .then((videoIds) => {
                const queryParams = new URLSearchParams();
                queryParams.set('id', videoIds[0].toString());
                queryParams.set('showTranslationModal', '1');
                if (languageOption) {
                    queryParams.set('translateTo', languageOption.value);
                    queryParams.set('autoTranslate', '1');
                }
                Navigation.openNewTab(Urls.APP + '?' + queryParams.toString());
            })
            .catch((err: unknown) => {
                if (!isVideoLimitError(err)) {
                    Toast.error(
                        'Something went wrong when trying to translate your video, please try again.',
                    );
                }
            })
            .finally(() => setIsOpening(false));
    }, [duplicateVideos, videoId, isOpening, languageOption]);

    if (!result || result.startsWith('Error')) {
        return null;
    }

    const calloutText = languageOption
        ? `A copy of your video will open in a new tab and start translating to ${optionLabel(languageOption)}.`
        : 'A copy of your video will open in a new tab, where you can choose a language to translate into.';

    return (
        <div className="my-l5-8">
            <Callout
                msgType={CALLOUT_TYPES.PROMO}
                text={calloutText}
                ctaText={isOpening ? 'Opening…' : 'Continue'}
                onCtaClick={handleOpenClick}
            />
        </div>
    );
};

export class TranslateVideoTool extends Tool<typeof translateVideoSchema> {
    protected override readonly schema = translateVideoSchema;
    protected override readonly toolName = TOOL_NAMES.translateVideo;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription =
        'Translate the current video into another language. This does NOT translate the video in ' +
        'place: it makes a duplicate copy and opens that copy in a NEW browser tab, where the text, ' +
        'captions and AI voiceovers are translated. The current video is left unchanged. Pass the ' +
        'language the user asked for as target_language so the translation can start automatically. ' +
        'Use this whenever the user asks for the video in another language instead of translating ' +
        'the text yourself. IMPORTANT: keep replying in the language the user is writing to you in — ' +
        'translating the video does not change the language of your own replies.';

    public override customRenderer = TranslateVideoToolRenderer;
    public override customRendererMode = CustomRendererMode.FULL;

    public override getToolDisplayName(): string {
        return 'Translating video';
    }

    public get statusIcon(): string {
        return 'translate';
    }

    public async execute(
        args: TranslateVideoArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const state = this.getState();

        if (!getCanTranslateVideos(state)) {
            return {
                result: `Error: The user has used all the video translations included in their current plan. Use the ${TOOL_NAMES.suggestUpgrade} tool with feature "translate", and phrase the reason as the user being out of video translations and able to upgrade for more (e.g. "You're out of video translations — upgrade for more."). Do NOT say translation is unavailable or requires a paid plan: every plan can translate, this user has just reached their limit.`,
                isSuccess: false,
            };
        }

        if (getVideoId(state) === null) {
            return {
                result: 'Error: There is no saved video to translate yet.',
                isSuccess: false,
            };
        }

        const languageOption = resolveLanguageOption(
            getTranslationSelectValues(state, false),
            args.target_language,
        );

        const replyLanguageReminder =
            'Reply to the user in the language they are writing to you in — do not switch to the ' +
            'language being translated into.';

        if (languageOption) {
            const languageLabel = optionLabel(languageOption);
            return {
                result:
                    `A button has been shown to the user. When they click it, a copy of the video ` +
                    `opens in a new tab and starts translating into ${languageLabel}; the ` +
                    `original video is unchanged. Briefly tell the user to click the button to ` +
                    `translate into ${languageLabel}. ${replyLanguageReminder}`,
                isSuccess: true,
            };
        }

        return {
            result:
                'A button has been shown to the user. When they click it, a copy of the video opens ' +
                'in a new tab where they choose the target language; the original video is unchanged. ' +
                `Briefly tell the user to click the button and pick a language. ${replyLanguageReminder}`,
            isSuccess: true,
        };
    }
}
